  // assets/weekly-drink-plan.js
  // Weekly Drink Plan - Allocate cart items across weekdays

  document.addEventListener('DOMContentLoaded', function() {
    'use strict';

    // ============================================
    // CONSTANTS
    // ============================================
    
    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const SAVE_DEBOUNCE_MS = 500;
    
    // ============================================
    // PLAN STATE
    // ============================================
    
    let deliveryPlan = {
      mon: {},
      tue: {},
      wed: {},
      thu: {},
      fri: {},
      sat: {},
      sun: {}
    };
    
    let saveTimeout = null;
    let isUpdating = false;
    
    // ============================================
    // PLAN UI BUILD
    // ============================================
    
    function buildPlanUI() {
      const panel = document.getElementById('weekly-drink-plan-panel');
      if (!panel) return;
      
      // Show panel if cart has items
      const cartItems = document.querySelectorAll('.cart-item');
      if (cartItems.length === 0) {
        panel.style.display = 'none';
        return;
      }
      
      panel.style.display = 'block';
      
      // Build header with status chips
      buildPlanHeader(panel);
      
      // Build days container
      const daysContainer = document.getElementById('plan-days-container');
      if (!daysContainer) return;
      
      daysContainer.innerHTML = '';
      
      DAYS.forEach(function(day, index) {
        const dayKey = DAY_KEYS[index];
        const dayCard = createDayCard(day, dayKey);
        daysContainer.appendChild(dayCard);
      });
      
      // Update totals and status
      updatePlanTotals();
      
      // Initialize event delegation for add buttons
      initializeAddButtonHandlers();
      
      // Setup checkout guard
      const remaining = getRemainingToAllocate();
      updateCheckoutLock(0, 0); // Will use getRemainingToAllocate() internally
      setupCheckoutGuard();
      
      // CRITICAL: Update checkout button state after UI is built
      if (typeof window.updateCheckoutButtonState === 'function') {
        console.log('[WeeklyPlan] buildPlanUI: Calling updateCheckoutButtonState');
        window.updateCheckoutButtonState();
        setTimeout(function() {
          window.updateCheckoutButtonState();
        }, 50);
        setTimeout(function() {
          window.updateCheckoutButtonState();
        }, 200);
      }
    }
    
    function buildPlanHeader(panel) {
      let header = panel.querySelector('.weekly-plan__header');
      if (!header) {
        header = document.createElement('div');
        header.className = 'weekly-plan__header';
        panel.insertBefore(header, panel.firstChild);
      }
      
      // Title section
      let titleSection = header.querySelector('.weekly-plan__title-section');
      if (!titleSection) {
        titleSection = document.createElement('div');
        titleSection.className = 'weekly-plan__title-section';
        header.appendChild(titleSection);
      }
      titleSection.innerHTML = `
        <span class="weekly-plan__icon">📅</span>
        <h3 class="weekly-plan__title">Weekly Drink Plan</h3>
      `;
      
      // Chips section
      let chipsSection = header.querySelector('.weekly-plan__chips');
      if (!chipsSection) {
        chipsSection = document.createElement('div');
        chipsSection.className = 'weekly-plan__chips';
        header.appendChild(chipsSection);
      }
      chipsSection.innerHTML = `
        <span class="weekly-plan__chip weekly-plan__chip--counter" id="plan-counter-chip">
          Planned: <strong id="plan-total-planned">0</strong> / <strong id="plan-total-cart">0</strong>
        </span>
        <span class="weekly-plan__chip weekly-plan__chip--status" id="plan-status-chip">
          Ready for checkout
        </span>
      `;
      
      // Remaining summary
      let remainingSummary = header.querySelector('.weekly-plan__remaining-summary');
      if (!remainingSummary) {
        remainingSummary = document.createElement('div');
        remainingSummary.className = 'weekly-plan__remaining-summary';
        remainingSummary.id = 'remaining-to-allocate';
        header.appendChild(remainingSummary);
      }
      
      // Per-product remaining list
      let productList = header.querySelector('.weekly-plan__product-list');
      if (!productList) {
        productList = document.createElement('div');
        productList.className = 'weekly-plan__product-list';
        productList.id = 'plan-product-list';
        header.appendChild(productList);
      }
      
      // Helper text
      let helperText = header.querySelector('.weekly-plan__helper');
      if (!helperText) {
        helperText = document.createElement('p');
        helperText.className = 'weekly-plan__helper';
        helperText.textContent = 'Distribute your total drinks across different days.';
        header.appendChild(helperText);
      }
    }
    
    function createDayCard(dayName, dayKey) {
      // Use native <details> for reliable accordion
      const day = document.createElement('details');
      day.className = 'weekly-plan__day';
      day.dataset.dayKey = dayKey;
      
      // Summary (accordion header)
      const summary = document.createElement('summary');
      summary.className = 'weekly-plan__day-summary';
      
      const dayLeft = document.createElement('div');
      dayLeft.className = 'weekly-plan__day-left';
      
      const dayNameEl = document.createElement('span');
      dayNameEl.className = 'weekly-plan__day-name';
      dayNameEl.textContent = dayName;
      
      const dayBadge = document.createElement('span');
      dayBadge.className = 'weekly-plan__day-badge';
      dayBadge.id = `plan-day-total-${dayKey}`;
      dayBadge.textContent = '0';
      
      dayLeft.appendChild(dayNameEl);
      dayLeft.appendChild(dayBadge);
      
      // Add drink button (inside summary but won't toggle details)
      const addBtn = document.createElement('button');
      addBtn.type = 'button'; // CRITICAL: prevent form submission
      addBtn.className = 'weekly-plan__add';
      addBtn.dataset.dayKey = dayKey;
      addBtn.dataset.action = 'add';
      addBtn.textContent = 'Add drink'; // "+" will be added via CSS ::before
      addBtn.setAttribute('aria-label', `Add drink to ${dayName}`);
      
      summary.appendChild(dayLeft);
      summary.appendChild(addBtn);
      
      // Day body (accordion content)
      const dayBody = document.createElement('div');
      dayBody.className = 'weekly-plan__day-body';
      dayBody.id = `plan-day-body-${dayKey}`;
      
      const rowsContainer = document.createElement('div');
      rowsContainer.className = 'weekly-plan__rows';
      rowsContainer.id = `plan-day-rows-${dayKey}`;
      
      // Add existing rows for this day
      const dayPlan = deliveryPlan[dayKey] || {};
      const hasAllocations = Object.keys(dayPlan).length > 0 && Object.values(dayPlan).some(qty => qty > 0);
      
      Object.keys(dayPlan).forEach(function(variantId) {
        const qty = dayPlan[variantId];
        if (qty > 0) {
          const row = createDayRow(dayKey, variantId, qty);
          rowsContainer.appendChild(row);
        }
      });
      
      dayBody.appendChild(rowsContainer);
      
      day.appendChild(summary);
      day.appendChild(dayBody);
      
      // Open if has allocations
      if (hasAllocations) {
        day.open = true;
      }
      
      return day;
    }
    
    function createDayRow(dayKey, variantId, quantity) {
      const row = document.createElement('div');
      row.className = 'weekly-plan__row';
      row.dataset.dayKey = dayKey;
      row.dataset.variantId = variantId;
      
      // Get product title
      const cartItems = getCartItems();
      const productItem = cartItems.find(function(item) { return item.variantId === variantId; });
      const productTitle = productItem ? productItem.title : 'Unknown Product';
      
      // Product name (read-only, no dropdown to prevent duplicates)
      const productName = document.createElement('div');
      productName.className = 'weekly-plan__product-name';
      productName.textContent = productTitle;
      
      // Remaining hint
      const remainingHint = document.createElement('span');
      remainingHint.className = 'weekly-plan__remaining-hint';
      remainingHint.id = `plan-row-remaining-${dayKey}-${variantId}`;
      updateRowRemainingHint(dayKey, variantId, remainingHint);
      
      // Quantity stepper
      const stepper = document.createElement('div');
      stepper.className = 'weekly-plan__stepper';
      
      const minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'weekly-plan__stepper-btn weekly-plan__stepper-btn--minus';
      minusBtn.textContent = '−';
      minusBtn.setAttribute('aria-label', 'Decrease quantity');
      minusBtn.addEventListener('click', function() {
        const qtyInput = row.querySelector('.weekly-plan__stepper-input');
        let currentQty = parseInt(qtyInput.value, 10) || 1;
        if (currentQty > 1) {
          currentQty--;
          qtyInput.value = currentQty;
          updateRowQuantity(dayKey, variantId, currentQty);
        } else {
          // Remove row if qty becomes 0
          removeDayRow(dayKey, variantId);
        }
      });
      
      const qtyInput = document.createElement('input');
      qtyInput.type = 'number';
      qtyInput.className = 'weekly-plan__stepper-input';
      qtyInput.value = quantity || 1;
      qtyInput.min = 1;
      qtyInput.setAttribute('aria-label', 'Quantity');
      qtyInput.addEventListener('change', function() {
        const maxQty = Math.min(
          getRemainingGlobal() + getAllocatedVariantQty(variantId),
          getRemainingForVariant(variantId) + (deliveryPlan[dayKey][variantId] || 0)
        );
        const qty = Math.max(1, Math.min(maxQty, parseInt(this.value, 10) || 1));
        this.value = qty;
        updateRowQuantity(dayKey, variantId, qty);
      });
      
      const plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'weekly-plan__stepper-btn weekly-plan__stepper-btn--plus';
      plusBtn.textContent = '+';
      plusBtn.setAttribute('aria-label', 'Increase quantity');
      plusBtn.addEventListener('click', function() {
        const qtyInput = row.querySelector('.weekly-plan__stepper-input');
        let currentQty = parseInt(qtyInput.value, 10) || 1;
        const remainingGlobal = getRemainingGlobal();
        const remainingVariant = getRemainingForVariant(variantId);
        
        if (remainingGlobal > 0 && remainingVariant > 0) {
          currentQty++;
          qtyInput.value = currentQty;
          updateRowQuantity(dayKey, variantId, currentQty);
        }
      });
      
      stepper.appendChild(minusBtn);
      stepper.appendChild(qtyInput);
      stepper.appendChild(plusBtn);
      
      // Remove button
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'weekly-plan__remove';
      removeBtn.innerHTML = '🗑️';
      removeBtn.setAttribute('aria-label', 'Remove this allocation');
      removeBtn.addEventListener('click', function() {
        removeDayRow(dayKey, variantId);
      });
      
      // Product info wrapper
      const productInfo = document.createElement('div');
      productInfo.className = 'weekly-plan__product-info';
      productInfo.appendChild(productName);
      productInfo.appendChild(remainingHint);
      
      row.appendChild(productInfo);
      row.appendChild(stepper);
      row.appendChild(removeBtn);
      
      // Update stepper button states
      updateStepperButtons(row, dayKey, variantId);
      
      return row;
    }
    
    function updateRowRemainingHint(dayKey, variantId, hintEl) {
      if (!hintEl) return;
      const remaining = getRemainingForVariant(variantId);
      if (remaining > 0) {
        hintEl.textContent = `(remaining: ${remaining})`;
        hintEl.style.display = 'inline';
      } else {
        hintEl.textContent = '(fully allocated)';
        hintEl.style.display = 'inline';
      }
    }
    
    function updateStepperButtons(row, dayKey, variantId) {
      if (!row) return;
      const plusBtn = row.querySelector('.weekly-plan__stepper-btn--plus');
      const minusBtn = row.querySelector('.weekly-plan__stepper-btn--minus');
      
      const remainingGlobal = getRemainingGlobal();
      const remainingVariant = getRemainingForVariant(variantId);
      
      // Plus button: disabled if no remaining globally or for this variant
      if (plusBtn) {
        if (remainingGlobal <= 0 || remainingVariant <= 0) {
          plusBtn.disabled = true;
          plusBtn.setAttribute('aria-label', 'Cannot increase: all drinks allocated');
        } else {
          plusBtn.disabled = false;
          plusBtn.setAttribute('aria-label', 'Increase quantity');
        }
      }
      
      // Minus button: always enabled (can always decrease)
      if (minusBtn) {
        minusBtn.disabled = false;
      }
    }
    
    // Add a drink row to a specific day
    function addDayRow(dayKey, selectedVariantId) {
      console.log('[WeeklyPlan] addDayRow called for day:', dayKey, 'variant:', selectedVariantId);
      
      const rowsContainer = document.getElementById(`plan-day-rows-${dayKey}`);
      if (!rowsContainer) {
        console.warn('[WeeklyPlan] Rows container not found for day:', dayKey);
        return;
      }
      
      // Get variants with remaining > 0
      const cartItems = getCartItems();
      const availableVariants = cartItems.filter(function(item) {
        return getRemainingForVariant(item.variantId) > 0 && getRemainingGlobal() > 0;
      });
      
      if (availableVariants.length === 0) {
        showToast('All drinks are allocated. Reduce quantities to allocate more.', 'warn');
        return;
      }
      
      // If variantId provided, use it; otherwise use first available
      let variantId = selectedVariantId;
      
      if (!variantId) {
        // Use first available variant
        variantId = availableVariants[0].variantId;
      }
      
      // Check if variant already exists in this day
      const existingRow = rowsContainer.querySelector(`.weekly-plan__row[data-variant-id="${variantId}"]`);
      
      if (existingRow) {
        // Focus existing row and increment if allowed
        existingRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        existingRow.style.animation = 'pulse 0.5s ease';
        setTimeout(function() {
          existingRow.style.animation = '';
        }, 500);
        
        const qtyInput = existingRow.querySelector('.weekly-plan__stepper-input');
        const plusBtn = existingRow.querySelector('.weekly-plan__stepper-btn--plus');
        
        if (qtyInput && plusBtn && !plusBtn.disabled) {
          // Simulate plus button click
          plusBtn.click();
        }
        return;
      }
      
      // Create new row
      if (!deliveryPlan[dayKey]) {
        deliveryPlan[dayKey] = {};
      }
      
      const remainingGlobal = getRemainingGlobal();
      const remainingVariant = getRemainingForVariant(variantId);
      const initialQty = Math.min(1, remainingGlobal, remainingVariant);
      
      if (initialQty <= 0) {
        showToast('Cannot add: no remaining drinks available.', 'error');
        return;
      }
      
      deliveryPlan[dayKey][variantId] = initialQty;
      
      const row = createDayRow(dayKey, variantId, initialQty);
      rowsContainer.appendChild(row);
      
      // Open the day details when adding a row
      const day = document.querySelector(`details.weekly-plan__day[data-day-key="${dayKey}"]`);
      if (day && !day.open) {
        day.open = true;
      }
      
        updatePlanTotals();
        validateAndSync();
        savePlan();
        const remaining = getRemainingToAllocate();
      updateCheckoutLock(0, 0); // Will use getRemainingToAllocate() internally
      setupCheckoutGuard();
    }
    
    // Show variant picker - opens inline dropdown for user to select product
    function showVariantPicker(dayKey) {
      console.log('[WeeklyPlan] showVariantPicker called for day:', dayKey);
      
      // Remove any existing picker
      closeVariantPicker(dayKey);
      
      const cartItems = getCartItems();
      const availableVariants = cartItems.filter(function(item) {
        return getRemainingForVariant(item.variantId) > 0 && getRemainingGlobal() > 0;
      });
      
      if (availableVariants.length === 0) {
        showToast('All drinks are allocated.', 'warn');
        return;
      }
      
      // Open the day accordion
      const day = document.querySelector(`details.weekly-plan__day[data-day-key="${dayKey}"]`);
      if (day && !day.open) {
        day.open = true;
      }
      
      // Get the day body and rows container
      const dayBody = document.getElementById(`plan-day-body-${dayKey}`);
      const rowsContainer = document.getElementById(`plan-day-rows-${dayKey}`);
      
      if (!dayBody || !rowsContainer) {
        console.warn('[WeeklyPlan] Day body or rows container not found');
        return;
      }
      
      // Create picker container
      const pickerContainer = document.createElement('div');
      pickerContainer.className = 'weekly-plan__picker';
      pickerContainer.id = `plan-picker-${dayKey}`;
      pickerContainer.dataset.dayKey = dayKey;
      
      // Create select dropdown
      const select = document.createElement('select');
      select.className = 'weekly-plan__picker-select';
      select.id = `plan-picker-select-${dayKey}`;
      
      // Add placeholder option
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = 'Choose a drink...';
      placeholderOption.disabled = true;
      placeholderOption.selected = true;
      select.appendChild(placeholderOption);
      
      // Add available variants
      availableVariants.forEach(function(item) {
        const remaining = getRemainingForVariant(item.variantId);
        const option = document.createElement('option');
        option.value = item.variantId;
        option.textContent = `${item.title} — remaining ${remaining}`;
        select.appendChild(option);
      });
      
      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'weekly-plan__picker-actions';
      
      // Add button
      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'weekly-plan__picker-add';
      addButton.textContent = 'Add';
      addButton.disabled = true; // Disabled until selection
      
      // Cancel button
      const cancelButton = document.createElement('button');
      cancelButton.type = 'button';
      cancelButton.className = 'weekly-plan__picker-cancel';
      cancelButton.textContent = 'Cancel';
      
      // Enable Add button when selection is made
      select.addEventListener('change', function() {
        addButton.disabled = !this.value || this.value === '';
      });
      
      // Add button click handler
      addButton.addEventListener('click', function() {
        const selectedVariantId = select.value;
        if (!selectedVariantId) return;
        
        // Close picker
        closeVariantPicker(dayKey);
        
        // Add the selected variant
        addDayRow(dayKey, selectedVariantId);
      });
      
      // Cancel button click handler
      cancelButton.addEventListener('click', function() {
        closeVariantPicker(dayKey);
      });
      
      // Assemble picker
      buttonContainer.appendChild(addButton);
      buttonContainer.appendChild(cancelButton);
      
      pickerContainer.appendChild(select);
      pickerContainer.appendChild(buttonContainer);
      
      // Insert picker before rows container
      dayBody.insertBefore(pickerContainer, rowsContainer);
      
      // Focus the select
      setTimeout(function() {
        select.focus();
      }, 100);
    }
    
    // Close variant picker for a specific day
    function closeVariantPicker(dayKey) {
      const picker = document.getElementById(`plan-picker-${dayKey}`);
      if (picker) {
        picker.remove();
      }
    }
    
    function removeDayRow(dayKey, variantId) {
      const row = document.querySelector(`.weekly-plan__row[data-day-key="${dayKey}"][data-variant-id="${variantId}"]`);
      if (row) {
        row.remove();
        
        // Remove from plan
        if (deliveryPlan[dayKey] && deliveryPlan[dayKey][variantId]) {
          delete deliveryPlan[dayKey][variantId];
        }
        
        updatePlanTotals();
        validateAndSync();
        savePlan();
        const remaining = getRemainingToAllocate();
      updateCheckoutLock(0, 0); // Will use getRemainingToAllocate() internally
      setupCheckoutGuard();
    }
    }
    
    function updateRowQuantity(dayKey, variantId, quantity) {
      if (!deliveryPlan[dayKey]) {
        deliveryPlan[dayKey] = {};
      }
      
      // Enforce constraints
      const remainingGlobal = getRemainingGlobal() + (deliveryPlan[dayKey][variantId] || 0);
      const remainingVariant = getRemainingForVariant(variantId) + (deliveryPlan[dayKey][variantId] || 0);
      const maxAllowed = Math.min(remainingGlobal, remainingVariant);
      
      const constrainedQty = Math.max(0, Math.min(quantity, maxAllowed));
      
      if (constrainedQty === 0) {
        // Remove row if quantity becomes 0
        removeDayRow(dayKey, variantId);
        return;
      }
      
      deliveryPlan[dayKey][variantId] = constrainedQty;
      
      // Update UI
      const row = document.querySelector(`.weekly-plan__row[data-day-key="${dayKey}"][data-variant-id="${variantId}"]`);
      if (row) {
        const qtyInput = row.querySelector('.weekly-plan__stepper-input');
        if (qtyInput) {
          qtyInput.value = constrainedQty;
        }
        updateStepperButtons(row, dayKey, variantId);
        const remainingHint = row.querySelector('.weekly-plan__remaining-hint');
        if (remainingHint) {
          updateRowRemainingHint(dayKey, variantId, remainingHint);
        }
      }
      
        updatePlanTotals();
        validateAndSync();
        savePlan();
        const remaining = getRemainingToAllocate();
      updateCheckoutLock(0, 0); // Will use getRemainingToAllocate() internally
      setupCheckoutGuard();
    }
    
    // ============================================
    // PLAN STATE CALCULATIONS
    // ============================================
    
    // Get cart variant quantities as map { variantId: qty }
    function getCartVariantQuantities() {
      const cartItems = getCartItems();
      const variantMap = {};
      cartItems.forEach(function(item) {
        variantMap[item.variantId] = (variantMap[item.variantId] || 0) + item.quantity;
      });
      return variantMap;
    }
    
  // Get total cart quantity (T)
  function getTotalCartQty() {
    const variantMap = getCartVariantQuantities();
    return Object.values(variantMap).reduce(function(sum, qty) {
      return sum + qty;
    }, 0);
  }
  
  // MINIMUM ITEM CHECK (Minimum 3 drinks required)
  const MINIMUM_ITEMS_REQUIRED = 3;
  
  function getCartItemCount() {
    // Try to get cart item count from multiple sources
    // 1. Try from Shopify cart object (if available)
    if (typeof window.cart !== 'undefined' && window.cart && window.cart.item_count !== undefined) {
      return window.cart.item_count;
    }
    
    // 2. Try from cart API (latestCartData if available)
    if (typeof latestCartData !== 'undefined' && latestCartData && latestCartData.item_count !== undefined) {
      return latestCartData.item_count;
    }
    
    // 3. Fallback: Calculate from DOM
    const cartItems = getCartItems();
    return cartItems.reduce(function(total, item) {
      return total + item.quantity;
    }, 0);
  }
  
  function checkMinimumItems() {
    const itemCount = getCartItemCount();
    const isValid = itemCount >= MINIMUM_ITEMS_REQUIRED;
    const remaining = Math.max(0, MINIMUM_ITEMS_REQUIRED - itemCount);
    
    console.log('[WeeklyPlan] Minimum items check:', { itemCount, required: MINIMUM_ITEMS_REQUIRED, isValid, remaining });
    
    return {
      isValid: isValid,
      itemCount: itemCount,
      required: MINIMUM_ITEMS_REQUIRED,
      remaining: remaining
    };
  }
    
    // Get allocated quantity for a specific variant across all days
    function getAllocatedVariantQty(variantId) {
      let total = 0;
      DAY_KEYS.forEach(function(dayKey) {
        const dayPlan = deliveryPlan[dayKey] || {};
        total += dayPlan[variantId] || 0;
      });
      return total;
    }
    
    // Get planned total (P)
    function getPlannedTotal() {
      let total = 0;
      DAY_KEYS.forEach(function(dayKey) {
        const dayPlan = deliveryPlan[dayKey] || {};
        Object.keys(dayPlan).forEach(function(variantId) {
          total += dayPlan[variantId] || 0;
        });
      });
      return total;
    }
    
    // Get remaining global (T - P)
    function getRemainingGlobal() {
      return getTotalCartQty() - getPlannedTotal();
    }
    
    // Get remaining for a specific variant
    function getRemainingForVariant(variantId) {
      const cartVariantQty = getCartVariantQuantities()[variantId] || 0;
      const allocatedQty = getAllocatedVariantQty(variantId);
      return Math.max(0, cartVariantQty - allocatedQty);
    }
    
    // Get cart items with metadata
    function getCartItems() {
      const cartItems = [];
      const cartItemElements = document.querySelectorAll('.cart-item');
      
      cartItemElements.forEach(function(itemEl) {
        const lineItem = parseInt(itemEl.dataset.lineItem, 10);
        const titleEl = itemEl.querySelector('.cart-item-title a');
        const qtyInput = itemEl.querySelector('.qty-input');
        
        if (titleEl && qtyInput) {
          // Get variant ID from data attribute (set in HTML template)
          const variantId = qtyInput.dataset.variantId || String(lineItem);
          const title = titleEl.textContent.trim();
          const variantEl = itemEl.querySelector('.cart-item-variant');
          const variantTitle = variantEl ? variantEl.textContent.trim() : '';
          const fullTitle = variantTitle ? `${title} - ${variantTitle}` : title;
          const quantity = parseInt(qtyInput.value, 10) || 0;
          
          cartItems.push({
            lineItem: lineItem,
            variantId: variantId,
            title: fullTitle,
            quantity: quantity
          });
        }
      });
      
      return cartItems;
    }
    
    // Enhanced: Get cart items from Shopify API (for accurate variant IDs)
    function getCartItemsFromAPI() {
      return fetch('/cart.js')
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to fetch cart');
          return response.json();
        })
        .then(function(cart) {
          return cart.items.map(function(item, index) {
            return {
              lineItem: index + 1,
              variantId: String(item.variant_id),
              title: item.product_title + (item.variant_title && item.variant_title !== 'Default Title' ? ' - ' + item.variant_title : ''),
              quantity: item.quantity
            };
          });
        })
        .catch(function(error) {
          console.error('[Weekly Plan] Failed to fetch cart from API:', error);
          // Fallback to DOM-based method
          return getCartItems();
        });
    }
    
    // Legacy aliases for backward compatibility
    function getTotalCartQuantity() {
      return getTotalCartQty();
    }
    
    function getTotalPlannedQuantity() {
      return getPlannedTotal();
    }
    
    function getDayTotal(dayKey) {
      const dayPlan = deliveryPlan[dayKey] || {};
      let total = 0;
      Object.keys(dayPlan).forEach(function(variantId) {
        total += dayPlan[variantId] || 0;
      });
      return total;
    }
    
    function updatePlanTotals() {
      const totalCart = getTotalCartQty();
      const totalPlanned = getPlannedTotal();
      const remaining = getRemainingGlobal();
      
      // Update counter chip
      const plannedEl = document.getElementById('plan-total-planned');
      const cartEl = document.getElementById('plan-total-cart');
      
      if (plannedEl) plannedEl.textContent = totalPlanned;
      if (cartEl) cartEl.textContent = totalCart;
      
      // Update remaining summary
      const remainingSummary = document.getElementById('remaining-to-allocate');
      if (remainingSummary) {
        // Update data-remaining attribute (source of truth for getRemaining())
        const oldRemaining = parseInt(remainingSummary.dataset.remaining || '0', 10);
        remainingSummary.setAttribute('data-remaining', remaining.toString());
        
        if (remaining > 0) {
          remainingSummary.innerHTML = `<strong>Remaining to allocate: ${remaining}</strong>`;
          remainingSummary.style.display = 'block';
        } else {
          remainingSummary.innerHTML = '';
          remainingSummary.style.display = 'none';
          remainingSummary.setAttribute('data-remaining', '0');
        }
        
        // CRITICAL: If remaining changed, update checkout button immediately
        if (oldRemaining !== remaining) {
          console.log('[WeeklyPlan] Remaining changed from', oldRemaining, 'to', remaining);
          
          // Dispatch event for cart.liquid
          document.dispatchEvent(new CustomEvent('weekly-plan:updated', { 
            detail: { remaining: remaining, shouldLock: remaining > 0 } 
          }));
          
          // ALSO directly call updateCheckoutButtonState if available (immediate update)
          if (typeof window.updateCheckoutButtonState === 'function') {
            console.log('[WeeklyPlan] Directly calling window.updateCheckoutButtonState');
            window.updateCheckoutButtonState();
            // Also call with small delays to ensure it works
            setTimeout(function() {
              if (typeof window.updateCheckoutButtonState === 'function') {
                window.updateCheckoutButtonState();
              }
            }, 10);
            setTimeout(function() {
              if (typeof window.updateCheckoutButtonState === 'function') {
                window.updateCheckoutButtonState();
              }
            }, 50);
          } else {
            console.warn('[WeeklyPlan] window.updateCheckoutButtonState not available yet');
          }
        }
      }
      
      // Update per-product remaining list
      updateProductRemainingList();
      
      // Update day totals (badges)
      DAY_KEYS.forEach(function(dayKey) {
        const dayTotal = getDayTotal(dayKey);
        const dayBadge = document.getElementById(`plan-day-total-${dayKey}`);
        if (dayBadge) {
          dayBadge.textContent = dayTotal;
          // Update badge visibility
          if (dayTotal > 0) {
            dayBadge.style.display = 'inline-flex';
          } else {
            dayBadge.style.display = 'none';
          }
        }
      });
      
      // Update all row stepper buttons (guard if function not defined)
      if (typeof updateAllStepperButtons === 'function') {
        updateAllStepperButtons();
      }
      
      // Update status chip (guard if function not defined)
      if (typeof updateStatusChip === 'function') {
        updateStatusChip(totalPlanned, totalCart);
      }
      
      // Update checkout lock
      // Update checkout lock using remaining as source of truth
      updateCheckoutLock(totalPlanned, totalCart);
      
      // 5) Also directly update checkout button state (after totals change)
      updateCheckoutButtonState();
      
      // CRITICAL: Also call cart.liquid's updateCheckoutButtonState if available (for subscription checkbox check)
      if (typeof window.updateCheckoutButtonState === 'function') {
        console.log('[WeeklyPlan] updatePlanTotals: Calling window.updateCheckoutButtonState, remaining:', remaining);
        window.updateCheckoutButtonState();
        setTimeout(function() {
          if (typeof window.updateCheckoutButtonState === 'function') {
            window.updateCheckoutButtonState();
          }
        }, 10);
        setTimeout(function() {
          if (typeof window.updateCheckoutButtonState === 'function') {
            window.updateCheckoutButtonState();
          }
        }, 50);
      }
    }
    
    function updateProductRemainingList() {
      const productList = document.getElementById('plan-product-list');
      if (!productList) return;
      
      const cartItems = getCartItems();
      const variantMap = getCartVariantQuantities();
      
      let html = '';
      cartItems.forEach(function(item) {
        const variantId = item.variantId;
        const cartQty = variantMap[variantId] || 0;
        const allocatedQty = getAllocatedVariantQty(variantId);
        const remainingQty = getRemainingForVariant(variantId);
        
        if (cartQty > 0) {
          html += `<div class="weekly-plan__product-item">
            <strong>${item.title}</strong>: ${cartQty} total • ${allocatedQty} allocated • ${remainingQty} remaining
          </div>`;
        }
      });
      
      if (html) {
        productList.innerHTML = html;
        productList.style.display = 'block';
      } else {
        productList.innerHTML = '';
        productList.style.display = 'none';
      }
    }
    
    function updateAllStepperButtons() {
      const rows = document.querySelectorAll('.weekly-plan__row');
      rows.forEach(function(row) {
        const dayKey = row.dataset.dayKey;
        const variantId = row.dataset.variantId;
        updateStepperButtons(row, dayKey, variantId);
        
        // Update remaining hints
        const remainingHint = row.querySelector('.weekly-plan__remaining-hint');
        if (remainingHint) {
          updateRowRemainingHint(dayKey, variantId, remainingHint);
        }
      });
      
      // Update "Add drink" buttons
      DAY_KEYS.forEach(function(dayKey) {
        const addBtn = document.querySelector(`[data-action="add"][data-day-key="${dayKey}"]`);
        if (addBtn) {
          const remaining = getRemainingGlobal();
          if (remaining <= 0) {
            addBtn.disabled = true;
            addBtn.textContent = 'All drinks allocated';
            addBtn.setAttribute('aria-label', 'All drinks allocated');
          } else {
            addBtn.disabled = false;
            addBtn.textContent = '+ Add drink';
            addBtn.setAttribute('aria-label', `Add drink to ${DAYS[DAY_KEYS.indexOf(dayKey)]}`);
          }
        }
      });
    }
    
    // ============================================
    // VALIDATION & CHECKOUT LOCK
    // ============================================
    
    // Validation message removed - status chip now handles this
    function updateValidationMessage(planned, total) {
      // Status chip in header now shows validation state
    }
    
    // ============================================
    // HARD CHECKOUT LOCK - TWO LAYERS
    // ============================================
    
    // Get remaining to allocate (source of truth from data-remaining attribute)
    function getRemaining() {
      const el = document.querySelector('#remaining-to-allocate');
      if (!el) return 0;
      const n = parseInt(el.dataset.remaining || '0', 10);
      return Number.isFinite(n) ? n : 0;
    }
    
    // Alias for backward compatibility
    function getRemainingToAllocate() {
      return getRemaining();
    }
    
    // LAYER 1: Update checkout UI (disable/enable)
    // Find checkout button element (theme-agnostic)
    function findCheckoutButton() {
      const selectors = [
        'button[name="checkout"]',
        '#checkout',
        'a[href*="/checkout"]',
        '.cart__checkout-button',
        '.checkout-button',
        '.btn-cart-checkout'
      ];
      
      for (const selector of selectors) {
        const el = document.querySelector(selector);
        if (el) {
          return el;
        }
      }
      return null;
    }
    
    // A) Find subscription confirmation checkbox
    function findSubscriptionCheckbox() {
      // Primary selector
      let subConfirm = document.querySelector('.subscription-details input[type="checkbox"]');
      
      // Fallback: find by ID or text content
      if (!subConfirm) {
        subConfirm = document.getElementById('subscription-agreement');
      }
      
      if (!subConfirm) {
        subConfirm = Array.from(document.querySelectorAll('input[type="checkbox"]'))
          .find(cb => cb.closest('.subscription-details') || 
                      (cb.parentElement?.innerText || '').includes('weekly subscription'));
      }
      
      return subConfirm;
    }
    
  // Update checkout button state directly
  function updateCheckoutButtonState() {
    const remaining = getRemaining();
    const totalCart = getTotalCartQty();
    const totalPlanned = getPlannedTotal();
    
    // NEW: Minimum item check
    const minItemCheck = checkMinimumItems();
    const hasMinimumItems = minItemCheck.isValid;
    
    // C) Update lock condition: include subscription checkbox
    const shouldLockCheckoutFromPlan = (remaining > 0) || (totalPlanned !== totalCart);
    
    // A) Find subscription checkbox
    const subConfirm = findSubscriptionCheckbox();
    const isCheckboxChecked = subConfirm ? subConfirm.checked : false;
    
    // C) Combined lock condition - NOW INCLUDES MINIMUM ITEM CHECK
    const shouldLockCheckout = shouldLockCheckoutFromPlan || !isCheckboxChecked || !hasMinimumItems;
    
    // TEMP debug logs
    console.log('[WeeklyPlan] shouldLockCheckout', shouldLockCheckout, {
      remaining, 
      totalPlanned, 
      totalCart, 
      shouldLockCheckoutFromPlan,
      isCheckboxChecked,
      checkboxFound: !!subConfirm,
      hasMinimumItems: hasMinimumItems,
      itemCount: minItemCheck.itemCount,
      minRequired: minItemCheck.required
    });
      
      // B) Find checkout button
      const checkoutEl = findCheckoutButton();
      console.log('[WeeklyPlan] checkoutEl', checkoutEl);
      
      if (!checkoutEl) {
        console.warn('[WeeklyPlan] Checkout button not found');
        return;
      }
      
      // D) Apply lock/unlock
      if (shouldLockCheckout) {
        // Lock: disable button
        if (checkoutEl.tagName === 'BUTTON') {
          checkoutEl.setAttribute('disabled', 'disabled');
          checkoutEl.setAttribute('aria-disabled', 'true');
        } else if (checkoutEl.tagName === 'A') {
          checkoutEl.removeAttribute('href');
          checkoutEl.setAttribute('aria-disabled', 'true');
        }
        checkoutEl.classList.add('is-disabled', 'disabled');
        checkoutEl.style.pointerEvents = 'none';
        checkoutEl.style.opacity = '0.6';
        checkoutEl.style.cursor = 'not-allowed';
      } else {
        // E) Unlock: enable button
        if (checkoutEl.tagName === 'BUTTON') {
          checkoutEl.removeAttribute('disabled');
          checkoutEl.removeAttribute('aria-disabled');
        } else if (checkoutEl.tagName === 'A') {
          checkoutEl.setAttribute('href', '/checkout');
          checkoutEl.removeAttribute('aria-disabled');
        }
        checkoutEl.classList.remove('is-disabled', 'disabled');
        checkoutEl.style.pointerEvents = 'auto';
        checkoutEl.style.opacity = '1';
        checkoutEl.style.cursor = 'pointer';
      }
      
      // F) Block direct navigation (click guard)
      // Remove existing listener if any
      if (checkoutEl._subscriptionCheckListener) {
        checkoutEl.removeEventListener('click', checkoutEl._subscriptionCheckListener);
      }
      
      checkoutEl._subscriptionCheckListener = function(e) {
        // CRITICAL: Re-check conditions at click time (don't rely on closure)
        const currentRemaining = getRemaining();
        const currentTotalCart = getTotalCartQty();
        const currentTotalPlanned = getPlannedTotal();
        const currentShouldLockFromPlan = (currentRemaining > 0) || (currentTotalPlanned !== currentTotalCart);
        
        // Re-check minimum items
        const currentMinItemCheck = checkMinimumItems();
        const currentHasMinimumItems = currentMinItemCheck.isValid;
        
        // Re-find checkbox and check its current state
        const currentSubConfirm = findSubscriptionCheckbox();
        const currentIsCheckboxChecked = currentSubConfirm ? currentSubConfirm.checked : false;
        
        const currentShouldLock = currentShouldLockFromPlan || !currentIsCheckboxChecked || !currentHasMinimumItems;
        
        console.log('[WeeklyPlan] Click guard check:', {
          currentShouldLock,
          currentShouldLockFromPlan,
          currentIsCheckboxChecked,
          currentHasMinimumItems,
          currentItemCount: currentMinItemCheck.itemCount,
          currentRemaining,
          currentTotalPlanned,
          currentTotalCart
        });
        
        if (currentShouldLock) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Show appropriate warning based on what's missing
          let warningMessage = '';
          if (!currentHasMinimumItems) {
            warningMessage = `Minimum ${MINIMUM_ITEMS_REQUIRED} drinks required to checkout. You currently have ${currentMinItemCheck.itemCount} drink${currentMinItemCheck.itemCount !== 1 ? 's' : ''}.`;
          } else if (currentShouldLockFromPlan) {
            warningMessage = 'Please allocate your drinks to days before checkout.';
          } else if (!currentIsCheckboxChecked) {
            warningMessage = 'Please confirm you understand this is a weekly subscription before checkout.';
          }
          
          alert(warningMessage);
          
          // Scroll to appropriate element
          if (!currentHasMinimumItems) {
            // Scroll to top of cart to show items
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (!currentIsCheckboxChecked && currentSubConfirm) {
            currentSubConfirm.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              currentSubConfirm.focus();
            }, 300);
          }
          
          return false;
        }
      };
      
      checkoutEl.addEventListener('click', checkoutEl._subscriptionCheckListener, true);
    }
    
  function updateCheckoutLock(planned, total) {
    const remaining = getRemaining();
    const totalCart = getTotalCartQty();
    const totalPlanned = getPlannedTotal();
    
    // NEW: Check minimum items
    const minItemCheck = checkMinimumItems();
    const hasMinimumItems = minItemCheck.isValid;
    
    // 2) Single source of truth: shouldLockCheckout - NOW INCLUDES MINIMUM ITEM CHECK
    const shouldLockFromPlan = (remaining > 0) || (totalPlanned !== totalCart);
    const shouldLock = shouldLockFromPlan || !hasMinimumItems;
    
    // Dispatch event for cart.liquid to update checkout button state
    document.dispatchEvent(new CustomEvent('weekly-plan:updated', { 
      detail: { 
        remaining: remaining, 
        shouldLock: shouldLock,
        hasMinimumItems: hasMinimumItems,
        itemCount: minItemCheck.itemCount,
        minRequired: minItemCheck.required
      } 
    }));
    
    // 5) Also directly update checkout button state
    updateCheckoutButtonState();
    
    // Show/hide lock message
    let lockMessage = document.getElementById('checkout-lock-message');
    if (shouldLock) {
      if (!lockMessage) {
        // Create lock message if it doesn't exist
        lockMessage = document.createElement('div');
        lockMessage.id = 'checkout-lock-message';
        lockMessage.className = 'checkout-lock-message';
        lockMessage.style.cssText = 'margin-top: 12px; padding: 12px; background: #fff4d6; border: 1px solid rgba(217,119,6,.2); border-radius: 8px; font-size: 13px; color: #92400e;';
        
        const lockText = document.createElement('p');
        lockText.className = 'lock-message-text';
        lockText.style.cssText = 'margin: 0; font-weight: 600;';
        lockMessage.appendChild(lockText);
        
        // Insert before checkout button
        const checkoutBtn = findCheckoutButton();
        if (checkoutBtn && checkoutBtn.parentElement) {
          checkoutBtn.parentElement.insertBefore(lockMessage, checkoutBtn);
        } else {
          // Fallback: append to cart actions
          const cartActions = document.querySelector('.cart-actions');
          if (cartActions) {
            cartActions.appendChild(lockMessage);
          }
        }
      }
      
      const lockText = lockMessage.querySelector('.lock-message-text');
      if (lockText) {
        // Determine which message to show
        if (!hasMinimumItems) {
          lockText.innerHTML = `⚠️ Minimum ${minItemCheck.required} drinks required. You have ${minItemCheck.itemCount}. Add ${minItemCheck.remaining} more to checkout.`;
        } else if (shouldLockFromPlan) {
          lockText.textContent = 'Please allocate your drinks to days before checkout.';
        }
      }
      lockMessage.style.display = 'block';
    } else {
      if (lockMessage) {
        lockMessage.style.display = 'none';
      }
    }
    
    console.log('[WeeklyPlan] Checkout lock updated:', { 
      remaining, 
      shouldLock, 
      shouldLockFromPlan,
      hasMinimumItems,
      itemCount: minItemCheck.itemCount,
      totalPlanned, 
      totalCart 
    });
  }
    
    // LAYER 2: Narrow checkout guard (ONLY for checkout, does NOT interfere with qty buttons)
    function setupCheckoutGuard() {
      // Remove existing handlers if any
      if (window._checkoutClickGuard) {
        document.removeEventListener('click', window._checkoutClickGuard, true);
      }
      if (window._checkoutSubmitGuard) {
        document.removeEventListener('submit', window._checkoutSubmitGuard, true);
      }
      
    // Narrow click guard - ONLY for checkout links/buttons (must NOT interfere with qty buttons)
    window._checkoutClickGuard = function(e) {
      // CRITICAL: Ignore qty buttons completely
      if (e.target.closest('[data-qty-action]')) {
        return; // Allow qty buttons to work
      }
      
      // Only check checkout triggers
      const t = e.target.closest('a[href*="/checkout"], button[name="checkout"]');
      if (!t) return; // Not a checkout trigger, allow event
      
      const remaining = getRemaining();
      const minItemCheck = checkMinimumItems();
      const hasMinimumItems = minItemCheck.isValid;
      
      console.log('[WeeklyPlan] Checkout click detected, remaining:', remaining, 'hasMinimumItems:', hasMinimumItems);
      
      // Check both remaining and minimum items
      if (remaining > 0 || !hasMinimumItems) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (!hasMinimumItems) {
          showCheckoutWarning(remaining, minItemCheck);
        } else {
          showCheckoutWarning(remaining, null);
        }
        return false;
      }
    };
      
    // Narrow submit guard - ONLY for checkout button submits
    window._checkoutSubmitGuard = function(e) {
      const form = e.target;
      const submitter = e.submitter;
      
      // Check if submitter is checkout button
      const isCheckoutSubmit = submitter && (
        submitter.name === 'checkout' || 
        submitter.matches('button[name="checkout"]') ||
        submitter && submitter.classList && submitter.classList.contains('btn-cart-checkout')
      );
      
      if (!isCheckoutSubmit) return; // Not checkout submit, allow
      
      const remaining = getRemaining();
      const minItemCheck = checkMinimumItems();
      const hasMinimumItems = minItemCheck.isValid;
      
      console.log('[WeeklyPlan] Checkout form submit detected, remaining:', remaining, 'hasMinimumItems:', hasMinimumItems);
      
      if (remaining > 0 || !hasMinimumItems) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (!hasMinimumItems) {
          showCheckoutWarning(remaining, minItemCheck);
        } else {
          showCheckoutWarning(remaining, null);
        }
        return false;
      }
    };
      
      // Attach handlers
      document.addEventListener('click', window._checkoutClickGuard, true);
      document.addEventListener('submit', window._checkoutSubmitGuard, true);
      
      console.log('[WeeklyPlan] Narrow checkout guard initialized.');
    }
    
  // Show checkout warning message
  function showCheckoutWarning(remaining, minItemCheck) {
    let message = 'Please allocate your drinks to days before checkout.';
    
    // Determine message based on what's blocking checkout
    if (minItemCheck && !minItemCheck.isValid) {
      message = `⚠️ Minimum ${minItemCheck.required} drinks required. You have ${minItemCheck.itemCount}. Add ${minItemCheck.remaining} more to checkout.`;
    } else if (remaining > 0) {
      message = 'Please allocate your drinks to days before checkout.';
    }
    
    // Show inline message
    let lockMessage = document.getElementById('checkout-lock-message');
    if (!lockMessage) {
      lockMessage = document.createElement('div');
      lockMessage.id = 'checkout-lock-message';
      lockMessage.className = 'checkout-lock-message';
      lockMessage.style.cssText = 'margin-top: 12px; padding: 12px; background: #fff4d6; border: 1px solid rgba(217,119,6,.2); border-radius: 8px; font-size: 13px; color: #92400e;';
      
      const lockText = document.createElement('p');
      lockText.className = 'lock-message-text';
      lockText.style.cssText = 'margin: 0; font-weight: 600;';
      lockText.innerHTML = message;
      lockMessage.appendChild(lockText);
      
      // Insert before checkout button
      const checkoutBtn = document.querySelector('button[name="checkout"]');
      if (checkoutBtn && checkoutBtn.parentElement) {
        checkoutBtn.parentElement.insertBefore(lockMessage, checkoutBtn);
      } else {
        const cartActions = document.querySelector('.cart-actions');
        if (cartActions) {
          cartActions.appendChild(lockMessage);
        }
      }
    } else {
      const lockText = lockMessage.querySelector('.lock-message-text');
      if (lockText) {
        lockText.innerHTML = message;
      }
      lockMessage.style.display = 'block';
    }
    
    // Scroll to message
    if (lockMessage) {
      lockMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
    
    // ============================================
    // SYNC WITH CART QUANTITIES
    // ============================================
    
    function validateAndSync() {
      if (isUpdating) return;
      isUpdating = true;
      
      // Force refresh cart data from DOM (qty inputs should be updated by now)
      // Also try to get fresh data from API if available
      const totalCart = getTotalCartQty();
      const totalPlanned = getPlannedTotal();
      const variantMap = getCartVariantQuantities();
      const cartVariantIds = Object.keys(variantMap);
      
      console.log('[WeeklyPlan] validateAndSync:', { totalCart, totalPlanned, variantMap, cartVariantIds });
      
      // Remove allocations for variants that no longer exist in cart
      DAY_KEYS.forEach(function(dayKey) {
        const dayPlan = deliveryPlan[dayKey] || {};
        Object.keys(dayPlan).forEach(function(variantId) {
          if (!cartVariantIds.includes(variantId)) {
            delete deliveryPlan[dayKey][variantId];
            // Remove row from UI
            const row = document.querySelector(`.weekly-plan__row[data-day-key="${dayKey}"][data-variant-id="${variantId}"]`);
            if (row) row.remove();
          }
        });
      });
      
      // Check per-variant constraints and auto-reduce if needed
      cartVariantIds.forEach(function(variantId) {
        const cartQty = variantMap[variantId] || 0;
        const allocatedQty = getAllocatedVariantQty(variantId);
        
        if (allocatedQty > cartQty) {
          const excess = allocatedQty - cartQty;
          autoReduceVariant(variantId, excess);
        }
      });
      
      // Check global constraint
      const newPlanned = getPlannedTotal();
      if (newPlanned > totalCart) {
        const excess = newPlanned - totalCart;
        autoReducePlan(excess);
      }
      
      // Rebuild UI to reflect changes
      buildPlanUI();
      
      // 5) Update checkout button after validateAndSync completes
      updateCheckoutButtonState();
      
      isUpdating = false;
    }
    
    function autoReduceVariant(variantId, excess) {
      // Reduce from last day, last row for this variant
      for (let i = DAY_KEYS.length - 1; i >= 0 && excess > 0; i--) {
        const dayKey = DAY_KEYS[i];
        const dayPlan = deliveryPlan[dayKey] || {};
        
        if (dayPlan[variantId] && dayPlan[variantId] > 0) {
          const reduceBy = Math.min(excess, dayPlan[variantId]);
          dayPlan[variantId] -= reduceBy;
          excess -= reduceBy;
          
          if (dayPlan[variantId] === 0) {
            delete dayPlan[variantId];
          }
          
          // Update UI
          const row = document.querySelector(`.weekly-plan__row[data-day-key="${dayKey}"][data-variant-id="${variantId}"]`);
          if (row) {
            if (dayPlan[variantId] > 0) {
              const qtyInput = row.querySelector('.weekly-plan__stepper-input');
              if (qtyInput) {
                qtyInput.value = dayPlan[variantId];
              }
            } else {
              row.remove();
            }
          }
        }
      }
    }
    
    function autoReducePlan(excess) {
      let remainingExcess = excess;
      
      // Reduce from last day, last row
      for (let i = DAY_KEYS.length - 1; i >= 0 && remainingExcess > 0; i--) {
        const dayKey = DAY_KEYS[i];
        const dayPlan = deliveryPlan[dayKey] || {};
        const variantIds = Object.keys(dayPlan).reverse();
        
        for (let j = 0; j < variantIds.length && remainingExcess > 0; j++) {
          const variantId = variantIds[j];
          const currentQty = dayPlan[variantId] || 0;
          
          if (currentQty > 0) {
            const reduceBy = Math.min(remainingExcess, currentQty);
            dayPlan[variantId] = currentQty - reduceBy;
            remainingExcess -= reduceBy;
            
            // Update UI
            const row = document.querySelector(`.plan-day-row[data-day-key="${dayKey}"][data-variant-id="${variantId}"]`);
            if (row) {
              const qtyInput = row.querySelector('.plan-qty-input');
              if (qtyInput) {
                qtyInput.value = dayPlan[variantId];
              }
              
              // Remove row if quantity is 0
              if (dayPlan[variantId] === 0) {
                delete dayPlan[variantId];
                row.remove();
              }
            }
          }
        }
      }
      
      // Show toast message
      if (excess > 0) {
        showToast(`Auto-reduced plan by ${excess} to match cart total.`, 'warning');
      }
    }
    
    function showToast(message, type) {
      // Simple toast implementation
      // type can be: 'error', 'warn', 'warning', 'success'
      const isError = type === 'error';
      const isWarning = type === 'warn' || type === 'warning';
      
      const toast = document.createElement('div');
      toast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 1rem 1.5rem;
        background: ${isError ? '#ffebee' : isWarning ? '#fff8e1' : '#e8f5e9'};
        border: 1px solid ${isError ? '#e53935' : isWarning ? '#ffc107' : '#4caf50'};
        border-radius: 8px;
        color: ${isError ? '#c62828' : isWarning ? '#f57c00' : '#2e7d32'};
        font-size: 0.875rem;
        font-weight: 500;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        animation: slideIn 0.3s ease;
      `;
      toast.textContent = message;
      document.body.appendChild(toast);
      
      setTimeout(function() {
        toast.style.animation = 'slideOut 0.3s ease';
        setTimeout(function() {
          toast.remove();
        }, 300);
      }, 3000);
    }
    
    // ============================================
    // PERSISTENCE (CART ATTRIBUTES)
    // ============================================
    
    // Build human-readable plan text for order notes
    function buildPlanText(plan, cartItems) {
      const DAY_NAMES = {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun'
      };
      
      let lines = ['WEEKLY DRINK PLAN'];
      let hasAllocations = false;
      
      DAY_KEYS.forEach(function(dayKey) {
        const dayPlan = plan[dayKey] || {};
        const dayAllocations = [];
        
        Object.keys(dayPlan).forEach(function(variantId) {
          const qty = dayPlan[variantId] || 0;
          if (qty > 0) {
            const cartItem = cartItems.find(function(item) {
              return item.variantId === variantId;
            });
            const productName = cartItem ? cartItem.title : `Variant ${variantId}`;
            dayAllocations.push(`${productName} x${qty}`);
            hasAllocations = true;
          }
        });
        
        if (dayAllocations.length > 0) {
          lines.push(` ${DAY_NAMES[dayKey]}: ${dayAllocations.join(', ')}`);
        }
      });
      
      const totalPlanned = getPlannedTotal();
      const totalCart = getTotalCartQty();
      lines.push(` Total planned: ${totalPlanned}/${totalCart}`);
      
      if (!hasAllocations) {
        return 'WEEKLY DRINK PLAN\n No drinks allocated yet.';
      }
      
      return lines.join('\n');
    }
    
    // Build schedule string for a specific variant (e.g., "Mon x1, Tue x2")
    function buildVariantSchedule(variantId, plan) {
      const DAY_NAMES = {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun'
      };
      
      const scheduleParts = [];
      
      DAY_KEYS.forEach(function(dayKey) {
        const dayPlan = plan[dayKey] || {};
        const qty = dayPlan[variantId] || 0;
        if (qty > 0) {
          scheduleParts.push(`${DAY_NAMES[dayKey]} x${qty}`);
        }
      });
      
      return scheduleParts.length > 0 ? scheduleParts.join(', ') : null;
    }
    
    // Update line item properties with drink schedule
    function updateLineItemProperties() {
      return fetch('/cart.js')
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to fetch cart');
          return response.json();
        })
        .then(function(cart) {
          const cartItems = getCartItems();
          const updatePromises = [];
          
          cart.items.forEach(function(cartItem, index) {
            const variantId = String(cartItem.variant_id);
            const lineNumber = index + 1;
            const scheduleString = buildVariantSchedule(variantId, deliveryPlan);
            
            // Get existing properties
            const existingProps = cartItem.properties || {};
            const existingSchedule = existingProps['Drink schedule'];
            
            // Only update if schedule changed
            if (scheduleString !== existingSchedule) {
              const newProps = { ...existingProps };
              
              if (scheduleString) {
                // Add or update schedule
                newProps['Drink schedule'] = scheduleString;
              } else {
                // Remove schedule if no allocation
                delete newProps['Drink schedule'];
              }
              
              // Only update if properties actually changed
              const propsChanged = JSON.stringify(newProps) !== JSON.stringify(existingProps);
              
              if (propsChanged) {
                const updatePromise = fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify({
                    line: lineNumber,
                    properties: newProps
                  })
                })
                .then(function(response) {
                  if (!response.ok) {
                    throw new Error(`Failed to update line ${lineNumber}`);
                  }
                  return response.json();
                })
                .catch(function(error) {
                  console.error(`[Weekly Plan] Failed to update line ${lineNumber} properties:`, error);
                });
                
                updatePromises.push(updatePromise);
              }
            }
          });
          
          return Promise.all(updatePromises);
        })
        .catch(function(error) {
          console.error('[Weekly Plan] Failed to update line item properties:', error);
          return Promise.resolve();
        });
    }
    
    function savePlan() {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
      
      saveTimeout = setTimeout(function() {
        const planJson = JSON.stringify(deliveryPlan);
        const cartItems = getCartItems();
        
        // Build human-readable plan text
        let planText;
        try {
          planText = buildPlanText(deliveryPlan, cartItems);
        } catch (error) {
          console.error('[Weekly Plan] Failed to build plan text:', error);
          planText = 'WEEKLY DRINK PLAN\n Plan not available';
        }
        
        // Validate JSON
        let validPlanJson = planJson;
        try {
          JSON.parse(planJson);
        } catch (error) {
          console.error('[Weekly Plan] Invalid plan JSON, resetting:', error);
          validPlanJson = '{}';
          planText = 'WEEKLY DRINK PLAN\n Plan not available';
        }
        
        // LAYER 1 & 2: Update cart note and attributes
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            note: planText,
            attributes: {
              delivery_plan: validPlanJson,
              delivery_plan_summary: planText
            }
          })
        })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to save plan');
          }
          return response.json();
        })
        .then(function(cart) {
          console.log('[Weekly Plan] Plan saved successfully (note + attributes)');
          
          // LAYER 3: Update line item properties (async, don't block)
          updateLineItemProperties()
            .then(function() {
              console.log('[Weekly Plan] Line item properties updated');
            })
            .catch(function(error) {
              console.error('[Weekly Plan] Failed to update line item properties:', error);
            });
        })
        .catch(function(error) {
          console.error('[Weekly Plan] Failed to save plan:', error);
        });
      }, SAVE_DEBOUNCE_MS);
    }
    
    function loadPlan() {
      fetch('/cart.js')
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to load cart');
          }
          return response.json();
        })
        .then(function(cart) {
          try {
            if (cart.attributes && cart.attributes.delivery_plan) {
              const loadedPlan = JSON.parse(cart.attributes.delivery_plan);
              
              // Validate structure
              if (typeof loadedPlan === 'object') {
                deliveryPlan = {
                  mon: loadedPlan.mon || {},
                  tue: loadedPlan.tue || {},
                  wed: loadedPlan.wed || {},
                  thu: loadedPlan.thu || {},
                  fri: loadedPlan.fri || {},
                  sat: loadedPlan.sat || {},
                  sun: loadedPlan.sun || {}
                };
              }
            }
          } catch (error) {
            console.error('[Weekly Plan] Failed to parse plan:', error);
            // Reset to empty plan
            deliveryPlan = {
              mon: {}, tue: {}, wed: {}, thu: {}, fri: {}, sat: {}, sun: {}
            };
          }
          
          // Rebuild UI with loaded plan
          buildPlanUI();
          validateAndSync();
          setupCheckoutGuard();
        })
        .catch(function(error) {
          console.error('[Weekly Plan] Failed to load cart:', error);
          buildPlanUI();
          setupCheckoutGuard();
        });
    }
    
    // ============================================
    // EVENT LISTENERS
    // ============================================
    
    // Listen for cart quantity changes
    document.addEventListener('change', function(e) {
      if (e.target && e.target.classList && e.target.classList.contains('qty-input')) {
        setTimeout(function() {
          validateAndSync();
          buildPlanUI();
          const remaining = getRemaining();
          updateCheckoutLock(0, 0); // Will use getRemaining() internally
          setupCheckoutGuard();
        }, 100);
      }
    });
    
    // Store latest cart data from events (single source of truth)
    let latestCartData = null;
    
    // Helper: Update DOM qty inputs from cart data (so getCartItems() reads correct values)
    function syncCartDataToDOM(cartData) {
      if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
        console.warn('[WeeklyPlan] Invalid cart data for DOM sync:', cartData);
        return;
      }
      
      cartData.items.forEach(function(item, index) {
        const lineNum = index + 1;
        const cartItem = document.querySelector(`.cart-item[data-line-item="${lineNum}"]`);
        if (cartItem) {
          const qtyInput = cartItem.querySelector(`.qty-input[data-line="${lineNum}"]`);
          if (qtyInput && item.quantity !== undefined) {
            qtyInput.value = item.quantity;
            console.log('[WeeklyPlan] Synced qty input for line', lineNum, 'to', item.quantity);
          }
        }
      });
    }
    
    // Listen for cart updates
    // 5) Listen to cart:updated event and update checkout button
    document.addEventListener('cart:updated', function(e) {
      const cartData = e.detail;
      console.log('[WeeklyPlan] cart:updated event received:', cartData);
      
      // Store as latest cart data (single source of truth)
      latestCartData = cartData;
      
      // Sync cart data to DOM qty inputs (so getCartItems() reads correct values)
      syncCartDataToDOM(cartData);
      
      // Force sync with fresh cart data
      setTimeout(function() {
        validateAndSync();
        buildPlanUI();
        const remaining = getRemaining();
        updateCheckoutLock(0, 0); // Will use getRemaining() internally
        setupCheckoutGuard();
        
        // 5) Update checkout button after cart update
        updateCheckoutButtonState();
      }, 100);
    });
    
    document.addEventListener('cart:change', function(e) {
      const cartData = e.detail;
      console.log('[WeeklyPlan] cart:change event received:', cartData);
      
      // Store as latest cart data
      latestCartData = cartData;
      
      // Sync cart data to DOM qty inputs
      syncCartDataToDOM(cartData);
      
      // Force sync with fresh cart data (immediately, no delay)
      validateAndSync();
      buildPlanUI();
      const remaining = getRemaining();
      updateCheckoutLock(0, 0); // Will use getRemaining() internally
      setupCheckoutGuard();
      
      console.log('[WeeklyPlan] ✅ UI updated after cart:change event, remaining:', remaining);
    });
    
    // Listen for cart totals updated (from cart.liquid renderSummary)
    document.addEventListener('cart:totals-updated', function(e) {
      const cartData = e.detail;
      console.log('[WeeklyPlan] cart:totals-updated event received:', cartData);
      
      // Store as latest cart data
      latestCartData = cartData;
      
      // Sync cart data to DOM qty inputs
      syncCartDataToDOM(cartData);
      
      // Force sync with fresh cart data (immediately, no delay)
      validateAndSync();
      buildPlanUI();
      const remaining = getRemaining();
      updateCheckoutLock(0, 0); // Will use getRemaining() internally
      setupCheckoutGuard();
      
      console.log('[WeeklyPlan] ✅ UI updated after cart:totals-updated event, remaining:', remaining);
    });
    
    // Listen for cart item removals
    const cartObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.removedNodes.length > 0) {
          setTimeout(function() {
            validateAndSync();
            buildPlanUI();
            setupCheckoutGuard();
          }, 100);
        }
      });
    });
    
    const cartContainer = document.querySelector('.cart-items');
    if (cartContainer) {
      cartObserver.observe(cartContainer, {
        childList: true,
        subtree: true
      });
    }
    
    // ============================================
    // ADD BUTTON EVENT DELEGATION (UI-ONLY)
    // ============================================
    
    function initializeAddButtonHandlers() {
      // Use .weekly-plan class selector (works with both ID and class)
      const planContainer = document.querySelector('.weekly-plan');
      if (!planContainer) {
        // Fallback to panel ID
        const panel = document.getElementById('weekly-drink-plan-panel');
        if (!panel) {
          console.warn('[WeeklyPlan] Plan container not found');
          return;
        }
        // Ensure it has the class for selector
        if (panel && panel.classList && !panel.classList.contains('weekly-plan')) {
          panel.classList.add('weekly-plan');
        }
        planContainer = panel;
      }
      
      // Remove any existing listeners to prevent duplicates
      // (We'll use a named function to allow removal if needed)
      if (planContainer._weeklyPlanClickHandler) {
        planContainer.removeEventListener('click', planContainer._weeklyPlanClickHandler, true);
      }
      
      // Use event delegation with CAPTURE phase for reliable early interception
      planContainer._weeklyPlanClickHandler = function(e) {
        // Handle add button clicks
        const addBtn = e.target.closest('[data-action="add"]');
        if (addBtn) {
          // Prevent default (form submission) and stop propagation (summary toggle)
          e.preventDefault();
          e.stopPropagation();
          
          const dayKey = addBtn.dataset.dayKey;
          if (!dayKey) {
            console.warn('[WeeklyPlan] Add button missing dayKey');
            return;
          }
          
          console.log('[WeeklyPlan] Add button clicked for day:', dayKey);
          
          // Open the parent details element
          const details = addBtn.closest('details.weekly-plan__day');
          if (details && !details.open) {
            details.open = true;
          }
          
          // Call existing add row logic - use showVariantPicker if available
          if (typeof showVariantPicker === 'function') {
            showVariantPicker(dayKey);
          } else if (typeof addDayRow === 'function') {
            // Fallback: call addDayRow (will use first available variant)
            addDayRow(dayKey);
          } else {
            console.error('[WeeklyPlan] Neither showVariantPicker nor addDayRow function found');
          }
          
          return;
        }
        
        // Prevent summary toggle when clicking interactive elements inside it
        const summary = e.target.closest('summary.weekly-plan__day-summary');
        if (summary) {
          // If clicking on button, select, input, or label, prevent summary toggle
          const interactiveEl = e.target.closest('button, select, input, label');
          if (interactiveEl) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
      };
      
      planContainer.addEventListener('click', planContainer._weeklyPlanClickHandler, true);
      console.log('[WeeklyPlan] Event delegation initialized');
    }
    
    // ============================================
    // INITIALIZATION
    // ============================================
    
    // Initialize event handlers immediately (before DOM is ready)
    // This ensures handlers are attached even if buildPlanUI runs later
    initializeAddButtonHandlers();
    
    // Load plan and build UI on page load
    loadPlan();
    
    // Also rebuild on window load (in case cart data loads later)
    // G) Listen for checkbox changes
    function setupSubscriptionCheckboxListener() {
      const subConfirm = findSubscriptionCheckbox();
      if (subConfirm) {
        // Remove existing listener if any
        if (subConfirm._weeklyPlanChangeListener) {
          subConfirm.removeEventListener('change', subConfirm._weeklyPlanChangeListener);
        }
        
        subConfirm._weeklyPlanChangeListener = function() {
          console.log('[WeeklyPlan] Subscription checkbox changed:', this.checked);
          validateAndSync();
          updateCheckoutButtonState();
        };
        
        subConfirm.addEventListener('change', subConfirm._weeklyPlanChangeListener);
        console.log('[WeeklyPlan] Subscription checkbox listener attached');
      } else {
        console.warn('[WeeklyPlan] Subscription checkbox not found, will retry on load');
      }
    }
    
    // Setup checkbox listener on load
    setupSubscriptionCheckboxListener();
    
    window.addEventListener('load', function() {
      // Retry checkbox listener setup in case checkbox loads late
      setupSubscriptionCheckboxListener();
      setTimeout(function() {
        loadPlan();
        // Re-initialize handlers after rebuild (safe, prevents duplicates)
        initializeAddButtonHandlers();
        setupCheckoutGuard();
        setupSubscriptionCheckboxListener(); // G) Retry checkbox listener setup
        updateCheckoutButtonState(); // Update checkout button state
      }, 500);
    });
    
    // Initial setup on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(function() {
          validateAndSync();
          buildPlanUI();
          const remaining = getRemaining();
          updateCheckoutLock(0, 0); // Will use getRemaining() internally
          setupCheckoutGuard();
          setupSubscriptionCheckboxListener(); // G) Setup checkbox listener
          updateCheckoutButtonState(); // Initial checkout button state
        }, 100);
      });
    } else {
      setTimeout(function() {
        validateAndSync();
        buildPlanUI();
        const remaining = getRemainingToAllocate();
        updateCheckoutLock(0, 0); // Will use getRemainingToAllocate() internally
        setupCheckoutGuard();
        setupSubscriptionCheckboxListener(); // G) Setup checkbox listener
        updateCheckoutButtonState(); // Initial checkout button state
      }, 100);
    }
    
  // Expose functions globally for cart.liquid to call
  window.getRemaining = getRemaining;
  window.getRemainingToAllocate = getRemainingToAllocate; // Alias for backward compatibility
  window.updateCheckoutLock = updateCheckoutLock;
  window.setupCheckoutGuard = setupCheckoutGuard;
  window.checkMinimumItems = checkMinimumItems; // NEW: Expose minimum item check
  window.getCartItemCount = getCartItemCount; // NEW: Expose item count getter
});

